import { Button } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { MomentFormMoment } from "../../../types/moment-form-moment";
import { type DialogController } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import { PublishButton } from "../publish-button";
import type { SaveButtonProps } from "../types";
import { PreviewDialog } from "./preview-dialog/preview-dialog";
import { PreviewSendTestButton } from "./preview-dialog/preview-send-test-button";

export const PreviewButton: FC<
  Omit<SaveButtonProps, "employeeCountState"> & {
    isNewsletter?: boolean;
    momentId: UUID;
    previewDialog: DialogController<{ isReview: boolean }>;
    showButton?: boolean;
  }
> = ({ previewDialog, showButton = true, ...rest }) => {
  const disabled = rest.formSaveState.formDisabled["preview-button"];
  const [saving, setSaving] = useState(false);
  const { getValues } = useFormContext<MomentFormMoment>();

  const handleClick = useCallback(async () => {
    setSaving(true);
    try {
      await rest.handleSave(getValues());
    } finally {
      setSaving(false);
    }
    previewDialog.handleOpen({ isReview: false });
  }, [rest, getValues, previewDialog]);

  return (
    <>
      {showButton && (
        <Button
          data-analytics-id="moment-preview-button"
          variant="outlined"
          disabled={disabled || !rest.saveDataReady}
          loading={saving}
          onClick={() => void handleClick()}
        >
          Save and Preview
        </Button>
      )}
      <PreviewDialog
        dialog={previewDialog}
        buttons={{
          sendTest: <PreviewSendTestButton {...rest} variant="text" />,
          publish: (
            <PublishButton
              formSaveState={rest.formSaveState}
              momentRestrictions={{ segment: [], email_from: [] }}
              handleSave={rest.handleSave}
              saveDataReady={rest.saveDataReady}
              review={{ value: false }}
              closePreviewDialog={() => previewDialog.handleClose()}
            />
          ),
        }}
        {...rest}
      />
    </>
  );
};
